import type * as CleaveModule from "cleave-zen";

import { FormatOptions } from "../models/formFields";
import { assertNever } from "../utils/never";

// cleave-zen is ESM-only and causes ERR_REQUIRE_CYCLE_MODULE during SSR
// module evaluation. We lazy-load it to defer evaluation to client-side
// runtime, matching the pattern used for flickity/lazysizes in carousel
// components (UGCCarousel, Gallery, CardSlider). Formatting functions are
// only called from client-side form handlers (useMountEffect, onChange),
// never during SSR rendering.
let _cleave: typeof CleaveModule | undefined;

/**
 * Load the cleave-zen module. Must be awaited before calling getFormatter().
 * Safe to call multiple times — only loads once.
 */
export const ensureCleaveLoaded = async (): Promise<void> => {
    _cleave ??= await import("cleave-zen");
};

/**
 * Wrapper class around cleave-zen functions
 */
class Formatter<
    T extends FormatOptions,
    U extends (value: string, options: Omit<T, "type">) => string,
> {
    private readonly _options: T;
    private readonly _format: U;
    private readonly _unformat: U | undefined;
    private readonly cursorTrackingDelim: CleaveModule.DelimiterType;

    public constructor({
        options,
        format,
        unformat,
        cursorTrackingDelim,
    }: {
        options: T;
        format: U;
        unformat?: U;
        cursorTrackingDelim: CleaveModule.DelimiterType;
    }) {
        this._options = options;
        this._format = format;
        this._unformat = unformat;
        this.cursorTrackingDelim = cursorTrackingDelim;
    }

    public format(value: string): string {
        return this._format(value, this._options);
    }

    public unformat(value: string): string {
        return this._unformat ? this._unformat(value, this._options) : value;
    }

    public registerCursorTracker(input: HTMLInputElement): void {
        if (_cleave) {
            _cleave.registerCursorTracker({
                input: input,
                delimiter: this.cursorTrackingDelim,
            });
        }
    }
}

// Passthrough formatter returned when cleave-zen is not yet loaded.
// This allows render-time calls (e.g. FormPhoneNumber's inputProps.value)
// to safely return unformatted values during SSR and initial client render.
const PASSTHROUGH_FORMATTER = new Formatter({
    options: { type: "general" } as FormatOptions,
    format: (value: string, _options: Omit<FormatOptions, "type">) => value,
    cursorTrackingDelim: " ",
});

export const getFormatter = (formatOptions: FormatOptions) => {
    if (!_cleave) {
        return PASSTHROUGH_FORMATTER;
    }
    switch (formatOptions.type) {
        case "credit-card": {
            return new Formatter({
                options: formatOptions,
                format: _cleave.formatCreditCard,
                unformat: _cleave.unformatCreditCard,
                cursorTrackingDelim: _cleave.DefaultCreditCardDelimiter,
            });
        }
        case "date": {
            return new Formatter({
                options: formatOptions,
                format: _cleave.formatDate,
                unformat: undefined,
                cursorTrackingDelim: _cleave.DefaultDateDelimiter,
            });
        }
        case "numeral": {
            return new Formatter({
                options: formatOptions,
                format: _cleave.formatNumeral,
                unformat: _cleave.unformatNumeral,
                cursorTrackingDelim: _cleave.DefaultNumeralDelimiter,
            });
        }
        case "general": {
            return new Formatter({
                options: formatOptions,
                format: _cleave.formatGeneral,
                unformat: _cleave.unformatGeneral,
                cursorTrackingDelim: " ",
            });
        }
        case "time": {
            return new Formatter({
                options: formatOptions,
                format: _cleave.formatTime,
                unformat: undefined,
                cursorTrackingDelim: _cleave.DefaultTimeDelimiter,
            });
        }
        default:
            throw assertNever(formatOptions);
    }
};
